import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { collegesAPI, applicationsAPI } from '../utils/api';
import { Clock, FileText, CheckCircle, AlertCircle, TrendingUp, BookOpen, Award, DollarSign, Send, Shield, Gift, GitCompare, GraduationCap, Calendar } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [myColleges, setMyColleges] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [collegesData, appsData] = await Promise.all([
        collegesAPI.getSaved(),
        applicationsAPI.getAll()
      ]);
      setMyColleges(collegesData);
      setApplications(appsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate overall profile completion from applications
  const calculateProfileCompletion = () => {
    if (applications.length === 0) return 0;
    const totalProgress = applications.reduce((sum, app) => sum + (app.progress || 0), 0);
    return Math.round(totalProgress / applications.length);
  };

  const profileCompletion = calculateProfileCompletion();

  // Get upcoming deadlines
  const getUpcomingDeadlines = () => {
    return myColleges
      .map(college => ({
        college: college.name,
        deadline: new Date(college.deadline)
      }))
      .sort((a, b) => a.deadline - b.deadline)
      .slice(0, 3);
  };

  const upcomingDeadlines = getUpcomingDeadlines();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-700">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
            Welcome back, {user.first_name || user.firstName}!
          </h1>
          <p className="text-gray-700 text-lg">
            Here's your application progress overview
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-emerald-100 to-teal-100 border-2 border-emerald-200 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-700 font-semibold mb-1">Profile Complete</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{profileCompletion}%</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-3 rounded-xl shadow-md">
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teal-100 to-cyan-100 border-2 border-teal-200 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-teal-700 font-semibold mb-1">Saved Colleges</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{myColleges.length}</p>
                </div>
                <div className="bg-gradient-to-br from-teal-500 to-cyan-500 p-3 rounded-xl shadow-md">
                  <BookOpen className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-100 to-emerald-100 border-2 border-cyan-200 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-cyan-700 font-semibold mb-1">In Progress</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-emerald-600 bg-clip-text text-transparent">
                    {applications.filter(a => a.status === 'in_progress').length}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-cyan-500 to-emerald-500 p-3 rounded-xl shadow-md">
                  <FileText className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-100 to-cyan-100 border-2 border-emerald-200 shadow-lg hover:shadow-xl transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-700 font-semibold mb-1">Submitted</p>
                  <p className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                    {applications.filter(a => a.status === 'submitted').length}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 p-3 rounded-xl shadow-md">
                  <CheckCircle className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Completion */}
            <Card>
              <CardHeader>
                <CardTitle>Complete Your Profile</CardTitle>
                <CardDescription>
                  Finish your application to apply to colleges
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Overall Progress</span>
                      <span className="text-sm font-medium text-blue-600">{profileCompletion}%</span>
                    </div>
                    <Progress value={profileCompletion} className="h-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <Link to="/application?section=personal">
                      <Button variant="outline" className="w-full justify-start">
                        Personal Info
                      </Button>
                    </Link>
                    <Link to="/application?section=family">
                      <Button variant="outline" className="w-full justify-start">
                        Family
                      </Button>
                    </Link>
                    <Link to="/application?section=education">
                      <Button variant="outline" className="w-full justify-start">
                        Education
                      </Button>
                    </Link>
                    <Link to="/application?section=testing">
                      <Button variant="outline" className="w-full justify-start">
                        Test Scores
                      </Button>
                    </Link>
                    <Link to="/application?section=activities">
                      <Button variant="outline" className="w-full justify-start">
                        Activities
                      </Button>
                    </Link>
                    <Link to="/application?section=essays">
                      <Button variant="outline" className="w-full justify-start">
                        Essays
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Applications in Progress */}
            <Card>
              <CardHeader>
                <CardTitle>Applications in Progress</CardTitle>
                <CardDescription>
                  Continue working on your applications
                </CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No applications started yet</p>
                    <Link to="/colleges">
                      <Button>Browse Colleges</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {applications.map(app => {
                      const college = myColleges.find(c => c._id === app.college_id);
                      if (!college) return null;
                      return (
                        <div key={app._id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center space-x-4">
                            <img src={college.image} alt={college.name} className="w-12 h-12 rounded object-cover" />
                            <div>
                              <h4 className="font-semibold">{college.name}</h4>
                              <p className="text-sm text-gray-600">
                                {app.status === 'submitted' ? 'Submitted' : `${app.progress}% Complete`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            {app.status === 'submitted' ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <Link to={`/application?app_id=${app._id}`}>
                                <Button size="sm">Continue</Button>
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Deadlines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Upcoming Deadlines</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingDeadlines.length === 0 ? (
                  <p className="text-sm text-gray-600">No deadlines yet</p>
                ) : (
                  <div className="space-y-3">
                    {upcomingDeadlines.map((item, index) => (
                      <div key={index} className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium">{item.college}</p>
                          <p className="text-xs text-gray-600">
                            {item.deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <AlertCircle className="h-4 w-4 text-orange-500" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/colleges" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Search Colleges
                  </Button>
                </Link>
                <Link to="/application" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    Continue Application
                  </Button>
                </Link>
                <Link to="/decisions" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Award className="h-4 w-4 mr-2" />
                    Track Decisions
                  </Button>
                </Link>
                <Link to="/scores" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Send className="h-4 w-4 mr-2" />
                    Score Sending
                  </Button>
                </Link>
                <Link to="/financial-aid" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Financial Aid
                  </Button>
                </Link>
                <Link to="/scholarships" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Gift className="h-4 w-4 mr-2" />
                    Scholarships
                  </Button>
                </Link>
                <Link to="/grade-reports" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <GraduationCap className="h-4 w-4 mr-2" />
                    Grade Reports
                  </Button>
                </Link>
                <Link to="/compare" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <GitCompare className="h-4 w-4 mr-2" />
                    Compare Colleges
                  </Button>
                </Link>
                <Link to="/my-colleges" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    View My Colleges
                  </Button>
                </Link>
                <Link to="/analytics" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    My Analytics
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-blue-900">Application Tip</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-blue-800">
                  Start your essays early! Most successful applicants spend 4-6 weeks refining their essays.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;